import { useEffect, useState } from "react";
import "./TrendsPage.css";
import { getSalaryBreakdown } from "./api";
import { fmt } from "./trendsFormat";

/**
 * SalaryBreakdownDialog
 *
 * React port of the shared Angular SalaryBreakdownDialogComponent - the
 * dialog behind the info icon on every salary cell in the three Trends
 * tables. One shared component, used by all three tabs (matching the old
 * app's single shared dialog rather than per-tab copies).
 *
 * Behavior ported:
 *   - opens with a request { minervaJobCode, territoryId, startMonth,
 *     startYear, endMonth, endYear, label } - months are NAMES ('Jan'),
 *     derived from the clicked row's review_period (see parseReviewPeriod);
 *   - loading skeleton of 5 rows while the call is in flight;
 *   - 2-column table TIME PERIOD | SALARY (£);
 *   - the row whose time_period is 'AVG' renders bold (the average row);
 *   - salary === 'No Data' renders as literal text, not currency.
 */
export default function SalaryBreakdownDialog({ open, request, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !request) return;
    setLoading(true);
    setError(null);
    setRows([]);
    getSalaryBreakdown({
      minervaJobCode: request.minervaJobCode,
      territoryId: request.territoryId,
      startMonth: request.startMonth,
      startYear: request.startYear,
      endMonth: request.endMonth,
      endYear: request.endYear,
    })
      .then((data) => {
        setRows(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load the salary breakdown.");
        setLoading(false);
      });
  }, [open, request]);

  if (!open) return null;

  return (
    <div className="tr-dialog-overlay" onClick={onClose} role="presentation">
      <div className="tr-dialog" role="dialog" aria-label="Salary breakdown" onClick={(e) => e.stopPropagation()}>
        <div className="tr-dialog-head">
          <div className="tr-dialog-title">{request?.label || "Salary breakdown"}</div>
          <button type="button" className="tr-dialog-close" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        {error && <p className="tr-status">{error}</p>}

        <table className="tr-table tr-breakdown-table">
          <thead>
            <tr>
              <th>TIME PERIOD</th>
              <th>SALARY (£)</th>
            </tr>
          </thead>
          <tbody>
            {loading &&
              [0, 1, 2, 3, 4].map((i) => (
                <tr key={`sk${i}`}>
                  <td><span className="tr-skeleton" /></td>
                  <td><span className="tr-skeleton" /></td>
                </tr>
              ))}
            {!loading &&
              rows.map((r, i) => {
                const isAvg = (r.time_period || "").toUpperCase() === "AVG";
                return (
                  <tr key={i} className={isAvg ? "tr-avg-row" : ""}>
                    <td>{r.time_period}</td>
                    <td>
                      {r.salary === "No Data"
                        ? <span className="tr-no-data">No Data</span>
                        : fmt(r.salary)}
                    </td>
                  </tr>
                );
              })}
            {!loading && !error && rows.length === 0 && (
              <tr><td colSpan="2" className="tr-status">No data for this period.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
